import {ResponseData} from "@/utils/response";
import http from "../utils/http-common";
import {OptionalData} from "@/utils/types";
import {streamChatSSE} from "@/utils/sse";

class TaskService {
    async getAllRecords(page = 1, per_page = 10) {
        return http.get<ResponseData>(`/tasks?page=${page}&per_page=${per_page}`);
    }

    // Lấy tối đa 100 bản ghi cho picker "Thêm cuộc họp vào project"
    // (FE lọc process_status === 'processed').
    async getProcessedRecords() {
        return http.get<ResponseData>(`/tasks?page=1&per_page=100`);
    }

    async getDetailTask(taskId: string) {
        return http.get<ResponseData>(
            `/tasks/${taskId}/view`
        );
    }

    async getShareToken(taskId: string) {
        return http.get<ResponseData>(
            `/tasks/${taskId}/share-token`
        );
    }

    async processTask(taskId: string, restart = false) {
        return http.post<ResponseData>(
            `/tasks/${taskId}/start`,
            { restart }
        );
    }

    async deleteTask(taskId: string) {
        return http.delete<ResponseData>(
            `/tasks/${taskId}`
        );
    }

    async updateTaskName(taskId: string, fileName: string) {
        return http.post<ResponseData>(
            `/tasks/${taskId}/update-name`, {
                file_name: fileName
            }
        );
    }

    async updateSpeakerName(taskId: string, speakerId: number | string, speakerLabel: string) {
        return http.post<ResponseData>(
            `/tasks/${taskId}/update-speaker-name`, {
                speaker_id: speakerId,
                speaker_label: speakerLabel
            }
        );
    }

    async getSummary(taskId: string) {
        return http.get<ResponseData>(
            `/tasks/${taskId}/summary`
        );
    }
    async updateSummary(taskId: string, summaryMode: 'template' | 'custom', typeSummary?: string, customPrompt?: string) {
        const payload: Record<string, string> = { summary_mode: summaryMode };
        if (summaryMode === 'template' && typeSummary) {
            payload.type_summary = typeSummary;
        } else if (summaryMode === 'custom' && customPrompt) {
            payload.custom_prompt = customPrompt;
        }
        
        return http.post<ResponseData>(
            `/tasks/${taskId}/update-summary`, payload
        );
    }

    async getSpeakerInfo(userId: string, taskId: string) {
        return http.get<ResponseData>(
            `/task/update_speaker?user_id=${userId}&task_id=${taskId}`
        );
    }

    async sendFeedback(data: OptionalData) {
        return http.post<ResponseData>(`/task/feedback`, data);
    }

    async updateSegmentText(taskId: string, data: OptionalData) {
        return http.post<ResponseData>(
            `/task/${taskId}/view`, data
        );
    }

    async updateSegment(taskId: string, segmentId: string | number, text: string) {
        return http.patch<ResponseData>(
            `/tasks/${taskId}/segments/${segmentId}`, {
                text: text
            }
        );
    }

    async downloadSegment(taskId: string | null, format: string) {
        return http.get<ResponseData>(`/task/view?task_id=${taskId}&format=${format}`, {responseType: 'blob'});
    }
    async uploadTask(domain: string, model: string, url: string | null = null, file: File | null = null) {
        const formData = new FormData();
        formData.append('domain', domain);
        formData.append('model', model);
        if (url) formData.append('url', url);
        if (file) formData.append('file', file);

        return http.post<ResponseData>('/task/new', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    async getChatSessions(taskId: string) {
        return http.get<ResponseData>(`/task/chat/sessions?task_id=${taskId}`);
    }

    async createChatSession(taskId: string) {
        return http.post<ResponseData>(`/task/chat/new_session`, { task_id: taskId });
    }

    async getChatHistory(sessionId: string) {
        return http.get<ResponseData>(`/task/chat/history?session_id=${sessionId}`);
    }

    async sendChatMessage(sessionId: string, message: string) {
        return http.post<ResponseData>(`/task/chat/send`, {
            session_id: sessionId,
            message
        });
    }

    /**
     * Gửi message chat và nhận phản hồi dạng STREAM (SSE).
     * Dùng fetch (axios trên browser không đọc được response body dạng stream).
     * `onDelta` được gọi mỗi khi có thêm 1 đoạn text; trả về Promise resolve khi stream kết thúc.
     */
    async sendChatMessageStream(
        sessionId: string,
        message: string,
        onDelta: (delta: string) => void,
        signal?: AbortSignal
    ): Promise<void> {
        return streamChatSSE(
            `/task/chat/send_stream`,
            {session_id: sessionId, message},
            onDelta,
            signal
        );
    }

    async deleteChatSession(sessionId: string) {
        return http.delete<ResponseData>(`/task/chat/session`, {
            params: {
                session_id: sessionId,
            }
        });
    }
}

export default new TaskService();
